/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: IntegrationsList.tsx
 🗓️ Created: 2026-01-24
 🧩 Description:
    Integrations list page - shows all integration connections
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Cloud,
    Briefcase,
    Activity,
    Hexagon,
    Package,
    Globe,
    Plus,
    Edit,
    Trash2,
    Calendar
} from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../contexts/NotificationContext';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { api } from '../../../lib/api';

interface IntegrationConnection {
    id: string;
    name: string;
    provider: string;
    status: string;
    authType: string;
    createdAt: string;
    updatedAt: string;
    settings?: Record<string, unknown>;
}

interface IntegrationType {
    id: number;
    slug: string;
    name: string;
    type: string;
    description: string;
    icon?: string;
    authTypes: string[];
    multiConnection: boolean;
    isActive: boolean;
}

const PROVIDER_ICONS: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
    hubspot: { icon: Hexagon, color: 'text-orange-500' },
    salesforce: { icon: Cloud, color: 'text-blue-500' },
    zoho: { icon: Briefcase, color: 'text-yellow-600' },
    pipedrive: { icon: Activity, color: 'text-green-500' },
    dynamics_365: { icon: Package, color: 'text-blue-700' },
    odoo: { icon: Globe, color: 'text-purple-600' },
    calendly: { icon: Calendar, color: 'text-cyan-500' },
    webhook: { icon: Globe, color: 'text-gray-500' },
    ldap: { icon: Package, color: 'text-blue-400' },
    active_directory: { icon: Package, color: 'text-blue-600' },
};

const PROVIDER_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
    HUBSPOT: { icon: Hexagon, color: 'text-orange-500' },
    SALESFORCE: { icon: Cloud, color: 'text-blue-500' },
    ZOHO: { icon: Briefcase, color: 'text-yellow-600' },
    PIPEDRIVE: { icon: Activity, color: 'text-green-500' },
    DYNAMICS_365: { icon: Package, color: 'text-blue-700' },
    ODOO: { icon: Globe, color: 'text-purple-600' },
    CALENDLY: { icon: Calendar, color: 'text-cyan-500' },
    WEBHOOK: { icon: Globe, color: 'text-gray-500' },
    LDAP: { icon: Package, color: 'text-blue-400' },
    ACTIVE_DIRECTORY: { icon: Package, color: 'text-blue-600' },
};

export const IntegrationsList = () => {
    const { t, i18n } = useI18n();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();

    const [connections, setConnections] = useState<IntegrationConnection[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; connection: IntegrationConnection | null }>({
        isOpen: false,
        connection: null,
    });

    // Search and pagination state
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Available providers from database
    const [availableProviders, setAvailableProviders] = useState<IntegrationType[]>([]);

    // Define columns
    const [columns, setColumns] = useState(() => [
        {
            key: 'name',
            label: t('integrations.columns.name'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: IntegrationConnection) => {
                const config = PROVIDER_CONFIG[item.provider] || { icon: Cloud, color: 'text-gray-500' };
                const Icon = config.icon;
                return (
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                            <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <span className="font-medium text-foreground">{item.name}</span>
                    </div>
                );
            },
        },
        {
            key: 'authType',
            label: t('integrations.columns.type'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: IntegrationConnection) => (
                <span className="text-muted-foreground">{item.authType || '-'}</span>
            ),
        },
        {
            key: 'provider',
            label: t('integrations.columns.provider'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: IntegrationConnection) => t(`integrations.providers.${item.provider?.toLowerCase()}.name`) || item.provider,
        },
        {
            key: 'status',
            label: t('integrations.columns.status'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: IntegrationConnection) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.status === 'CONNECTED'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {item.status === 'CONNECTED' ? t('integrations.status.connected') : t('integrations.status.disconnected')}
                </span>
            ),
        },
        {
            key: 'createdAt',
            label: t('integrations.columns.created'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: IntegrationConnection) => new Date(item.createdAt).toLocaleDateString(),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ]);

    // Fetch connections
    const fetchConnections = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/integrations/crm/connections');
            setConnections(response.data || []);
        } catch (err) {
            showError(err instanceof Error ? err.message : t('integrations.messages.loadError'));
        } finally {
            setLoading(false);
        }
    }, [showError, t]);

    // Fetch available integration types from database
    const fetchIntegrationTypes = useCallback(async () => {
        try {
            const response = await api.get('/api/integrations/crm/types');
            setAvailableProviders(response.data || []);
        } catch (err) {
            console.error('Failed to load integration types:', err);
        }
    }, []);

    useEffect(() => {
        fetchConnections();
        fetchIntegrationTypes();
    }, [fetchConnections, fetchIntegrationTypes]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...connections];

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(c =>
                c.name.toLowerCase().includes(query) ||
                c.provider.toLowerCase().includes(query)
            );
        }

        // Sort
        result.sort((a, b) => {
            const aVal = (a as unknown as Record<string, unknown>)[sortBy] ?? '';
            const bVal = (b as unknown as Record<string, unknown>)[sortBy] ?? '';
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return sortOrder === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [connections, searchQuery, sortBy, sortOrder]);

    // Paginated data
    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page, limit]);

    const pagination = useMemo(() => ({
        page,
        limit,
        total: filteredData.length,
        totalPages: Math.ceil(filteredData.length / limit),
    }), [page, limit, filteredData.length]);

    // Handlers
    const handleCreate = useCallback(() => {
        setShowAddModal(true);
    }, []);

    const handleEdit = useCallback((connection: IntegrationConnection) => {
        const providerLower = connection.provider.toLowerCase().replace('_', '-');
        navigate(`/settings/integrations/${providerLower}/${connection.id}`);
    }, [navigate]);

    const handleDelete = useCallback((connection: IntegrationConnection) => {
        setDeleteModal({ isOpen: true, connection });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.connection) return;

        try {
            await api.delete(`/api/integrations/crm/connections/${deleteModal.connection.id}`);
            showSuccess(t('integrations.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, connection: null });
            fetchConnections();
        } catch (error) {
            showError(error instanceof Error ? error.message : t('integrations.messages.deleteError'));
        }
    }, [deleteModal.connection, fetchConnections, showError, showSuccess, t]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(prev => prev.map(col =>
            col.key === key ? { ...col, visible: !col.visible } : col
        ));
    }, []);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    // Render actions for each row
    const renderActions = useCallback((connection: IntegrationConnection) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => handleEdit(connection)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(connection)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={paginatedData}
                loading={loading}
                columns={columns}
                onColumnToggle={handleColumnToggle}
                pagination={pagination}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                searchQuery={searchQuery}
                onSearch={handleSearch}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                renderActions={renderActions}
                searchPlaceholder={t('integrations.searchPlaceholder')}
                title={t('integrations.title')}
                titleIcon={<Cloud className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('integrations.actions.add')} position="top">
                            <button
                                onClick={handleCreate}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                            >
                                <Plus className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('integrations.actions.add')}</span>
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, connection: null })}
                onConfirm={confirmDelete}
                title={t('integrations.deleteConnection')}
                message={`${t('integrations.messages.confirmDelete')} (${deleteModal.connection?.name})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />

            {/* Add New Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card border border-border rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold text-foreground mb-4">
                            {t('integrations.selectProvider')}
                        </h2>
                        <div className="grid grid-cols-2 gap-3">
                            {availableProviders.map((provider) => {
                                const iconConfig = PROVIDER_ICONS[provider.slug] || { icon: Globe, color: 'text-gray-500' };
                                const IconComponent = iconConfig.icon;
                                return (
                                    <button
                                        key={provider.id}
                                        onClick={() => {
                                            setShowAddModal(false);
                                            navigate(`/settings/integrations/${provider.slug}/new`);
                                        }}
                                        className="flex items-center gap-3 p-4 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
                                    >
                                        <IconComponent className={`w-6 h-6 ${iconConfig.color}`} />
                                        <span className="font-medium text-foreground">{provider.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            onClick={() => setShowAddModal(false)}
                            className="mt-4 w-full py-2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
